import { Theme, linearProgressClasses } from '@mui/material'
import { createTheme } from '@mui/material/styles'

import { colors, componentSizes } from './variables'

const mainTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 1024,
      lg: 1200,
      xl: 1400,
    },
  },
  palette: {
    primary: {
      main: colors.primary,
      dark: colors.primaryDark,
    },
  },
  typography: {
    h1: {
      fontWeight: 700,
      fontSize: '32px',
      color: colors.black,
      lineHeight: '130%',
    },
    h2: {
      fontWeight: 700,
      fontSize: '20px',
      color: colors.black,
      lineHeight: '130%',
    },
    h3: {
      fontWeight: 700,
      fontSize: '15px',
      color: colors.black,
      lineHeight: '130%',
    },
    body1: {
      color: colors.bodyText,
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '130%',
    },
    caption: {
      color: colors.bodyText,
      fontSize: '14px',
      fontWeight: 700,
      lineHeight: '130%',
    },
  },

  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: colors.primary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          maxHeight: '42px',
          fontSize: '14px',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primaryDark,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.error,
          },
          '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.error,
          },
        },
        multiline: {
          maxHeight: 'unset',
          minHeight: '42px',
        },
        adornedEnd: {
          paddingRight: 0,
        },
        notchedOutline: {
          borderColor: colors.inputBorder,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '10px 14px',
          boxShadow: 'none',
          letterSpacing: 'normal',
          fontSize: 14,
          fontWeight: 400,
          lineHeight: 1.04,
          textTransform: 'none',
          minHeight: '36px',

          '&.MuiButton-outlined': {
            padding: '9px 13px',
          },

          '&:disabled': {
            backgroundColor: colors.lightGrey,
            color: colors.disabledFormControlText,

            '&.no-background': {
              backgroundColor: 'transparent',
            },
          },
        },
        sizeLarge: {
          padding: '14px 16px',

          '&.MuiButton-outlined': {
            padding: '13px 15px',
          },
        },
        sizeSmall: {
          padding: '6px 13px',

          '&.MuiButton-outlined': {
            padding: '5px 12px',
          },
        },
        contained: {
          color: colors.white,
          backgroundColor: colors.primary,
          '&:hover': {
            backgroundColor: colors.primaryDark,
            boxShadow: 'none',
          },
        },
        outlined: {
          '&:hover': {
            color: colors.primaryDark,
          },
        },
        text: {
          '&:hover': {
            color: colors.primaryDark,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.bodyText,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: ({ theme }: { theme: Theme }) => ({
          minWidth: '7px',
          height: '7px',
          backgroundColor: theme.palette.primary.main,
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: 0,
          flexDirection: 'row-reverse',
          alignItems: 'flex-start',
          minHeight: '20px',
          '&.Mui-expanded': {
            padding: '0 0 13px',
          },
        },
        content: {
          margin: '0',
          alignSelf: 'center',
        },
        expandIconWrapper: {
          color: colors.bodyText,
          transform: 'rotate(270deg)',
          '&.Mui-expanded': {
            transform: 'rotate(0deg)',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: '9px',
          borderRadius: '18px',
          minWidth: '92px',
          [`&.${linearProgressClasses.colorPrimary}`]: {
            backgroundColor: colors.darkGray,
          },
          [`& .${linearProgressClasses.bar}`]: {
            backgroundColor: colors.pink,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: 'Roboto',
          fontStyle: 'normal',
          fontSize: '14px',
          fontWeight: 700,
          padding: 0,
          marginRight: componentSizes.mdDistance,
          color: colors.black,
          '&.Mui-selected': {
            fontSize: '15px',
            color: colors.black,
            fontWeight: 700,
            cursor: 'default',
            pointerEvents: 'none',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          marginRight: 'unset',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: '4px',
          bottom: '5px',
        },
      },
    },
  },
})

export default mainTheme
